// ============================================================
// ThemeProvider
//
// Consumes a vertical's branding tokens and applies them as
// CSS variable overrides to a wrapper div. The wrapper inherits
// nothing else — the OLIVIUM design language stays intact, only
// the accent colors and wordmark surfaces change.
//
// Usage:
//   <ThemeProvider vertical={getVertical('prince-hall')}>
//     <SquareCodeShell />
//   </ThemeProvider>
//
// Pass either:
//   vertical:  full VerticalConfig (we read its `branding` field)
//   branding:  raw branding tokens object
// ============================================================

import { createContext, useContext, useMemo } from 'react'

const ThemeContext = createContext({
  vertical: null,
  branding: null,
})

export function useTheme() {
  return useContext(ThemeContext)
}

export default function ThemeProvider({ vertical = null, branding = null, children }) {
  const tokens = branding || vertical?.branding || null

  const cssVars = useMemo(() => {
    const vars = {}
    if (tokens?.primary)   vars['--olv-purple'] = tokens.primary
    if (tokens?.secondary) vars['--olv-cyan']   = tokens.secondary
    return vars
  }, [tokens])

  const value = useMemo(
    () => ({ vertical, branding: tokens }),
    [vertical, tokens]
  )

  return (
    <ThemeContext.Provider value={value}>
      <div style={{ ...cssVars, minHeight: '100%' }}>
        {children}
      </div>
    </ThemeContext.Provider>
  )
}
